import { Link } from "react-router-dom";
import { useSignedIn } from "../lib/useSignedIn";

/**
 * The public landing page, for someone who arrives at the domain without an account.
 *
 * Until now `/` was the sign-in form, so the only thing the internet saw of this business was a
 * password box -- no statement of what it is, who it is for, or why a skating family would want it.
 * That is also a concrete problem beyond first impressions: SES's reviewers follow the website URL on a
 * production-access request, and a bare login screen is one of the things that reads as an unverifiable
 * business (docs, CLAUDE.md).
 *
 * Deliberately omitted, and worth knowing why:
 *
 * - No testimonials. The prototype has two, attributed to named parents with quoted praise, and both
 *   are invented. Publishing invented endorsements from named people is not something to ship by
 *   default -- add them when there are real ones, with permission.
 * - No phone number or contact address. The prototype's are placeholders, and a wrong contact detail on
 *   a public page is worse than none.
 * - The Privacy and Terms links point at routes whose copy is the owner's to write; they are stubs.
 */
const SERVICES = [
  {
    title: "Jump strength",
    description:
      "Off-ice power development built around landing mechanics, rather than generic gym programming.",
  },
  {
    title: "Specific progressions",
    description:
      "Based on each individual skater’s needs, growth spurts, injury history, and goals.",
  },
  {
    title: "Flexibility focused",
    description:
      "Mobility going beyond static stretching to develop control and stability throughout the skater’s range of motion.",
  },
];

export function Home() {
  const { signedIn, destination } = useSignedIn();
  return (
    <div className="marketing">
      <section className="marketing-hero">
        <p className="marketing-eyebrow">Off-ice training · built for figure skaters</p>
        <h1>
          Strength off the ice.
          <br />
          Precision on it.
        </h1>
        <p className="marketing-lede">
          Individualised off-ice programming for figure skaters, with a coach-built plan, guided
          sessions, and progress a family can actually see.
        </p>
        {/*
          The only call to action, on purpose. There is no self-service signup: an account exists
          because a coach invited someone (PRD §4.1), so a "Sign up" button here would lead nowhere and
          teach visitors the wrong thing about how this works.
        */}
        {/* The root is what people type, bookmark, and land on from "open in new tab". It told an
            already-signed-in visitor to log in to the account they were logged into, which makes the app
            feel signed-out when it isn't -- and "is everything set up?" is the question they opened it
            to answer (UX review F18). While the local session check resolves, the signed-out wording
            stands: it is right far more often, and it never bounces anyone anywhere. */}
        {signedIn ? (
          <Link className="link-button marketing-cta" to={destination}>
            Go to your dashboard
          </Link>
        ) : (
          <>
            <Link className="link-button marketing-cta" to="/signin">
              Log in to your account
            </Link>
            <p className="marketing-note">
              New here? Accounts are set up by your coach — look for your invitation email.
            </p>
          </>
        )}
      </section>

      <section className="marketing-services" aria-label="What the programme covers">
        {SERVICES.map((service) => (
          <article key={service.title} className="marketing-card">
            <h2>{service.title}</h2>
            <p>{service.description}</p>
          </article>
        ))}
      </section>

      <footer className="marketing-footer">
        <p>Athlete&rsquo;s Advantage</p>
        <p className="marketing-footer-links">
          <Link to="/privacy">Privacy</Link>
          <span aria-hidden="true"> · </span>
          <Link to="/terms">Terms</Link>
        </p>
      </footer>
    </div>
  );
}
